// @flow
import queryString from 'query-string';
import isPointGeotype from 'utils/search/isPointGeotype';
import { urlStringSemantic } from 'utils/search/searchFacets';
import { NO_FILTER_SENTINEL, DEFAULT_SEARCH_RADIUS } from 'utils/constants';
import {
  filterNulls,
  updateObject,
  mapObject,
  filterObject,
  removeKeys,
  entries,
  isObject,
  mapValues,
  arrToObject,
  values
} from 'utils/functional';
import type { GradSortType, SortType } from 'redux/main/reducers/mainSearch';

type AllSearchTypes = ListingType | StaffTypeFilter;

export type FiltersWithType = SearchFiltersWithType<AllSearchTypes>;

export const makeSerializedUrlFilters = ({
  filters,
  searchFacets
}: {
  filters: SearchFilters | FiltersWithType,
  searchFacets: { [string]: SearchFacet }
}) =>
  removeKeys(
    updateObject(
      ...entries(
        filterObject(
          filters,
          // ALL is the default value so don't add it to the url
          // UNLESS it is the `type` param, where we use 'ALL'
          (key, value) => value !== 'ALL' || key === 'type'
        )
      ).map(([key, value]) => {
        const facet = searchFacets[key];
        if (facet && facet.type === 'date') {
          return value || {};
        }
        return facet
          ? facet.type === 'array'
            ? value.length === 0
              ? { [facet.name]: NO_FILTER_SENTINEL }
              : facet.options.every(i => value.includes(i))
              ? {} // will be removed: 'ALL' case
              : { [facet.name]: value } // TODO: this case shouldn't exist
            : { [facet.name]: value }
          : { [key]: value };
      })
    ),
    ['type']
  );

const getSearchUrl = ({
  filters,
  searchFacets,
  query,
  pageIndex,
  sort,
  location,
  radius,
  resultsView,
  landingPagePath,
  scrollToId,
  originatingPathname,
  currentSearchParams
}: {|
  filters: $ReadOnly<SearchFiltersWithType<AllSearchTypes>>,
  searchFacets: { [string]: SearchFacet },
  query: string,
  pageIndex: number,
  sort?: $ReadOnly<{ [AllSearchTypes]: SortType | GradSortType }>,
  location: SearchLocation,
  radius: string,
  currentSearchParams: string,
  resultsView?: ?string,
  searchPagePath?: string,
  landingPagePath?: ?string,
  scrollToId?: string,
  originatingPathname?: string
|}) => {
  const sortParam = (sort && sort[filters.type]) || null;
  let newUrl;
  let searchParams;

  if (landingPagePath) {
    newUrl = landingPagePath;
    searchParams = {
      resultsView,
      radius,
      sort: sortParam,
      q: query,
      page: pageIndex + 1
    };
  } else {
    newUrl = originatingPathname;
    // No query params for filters (except 'type') with a value of "ALL"
    const urlSerializedFilters = makeSerializedUrlFilters({
      filters,
      searchFacets
    });

    searchParams = {
      ...urlSerializedFilters,
      radius,
      sort: sortParam,
      resultsView,
      q: query,
      page: pageIndex + 1
    };
  }
  const searchFiltersWithNull = arrToObject(
    values(searchFacets).reduce(
      (acc, v) =>
        acc.concat(v.names ? v.names.map(n => [n, null]) : [[v.name, null]]),
      []
    )
  );
  const addedValues = mapValues(
    filterObject(searchParams, (key, value) => {
      if (
        (key === 'page' && value && parseInt(value, 10) < 2) ||
        (key === 'radius' &&
          (!location.latitude ||
            !isPointGeotype(location.geoType) ||
            value === DEFAULT_SEARCH_RADIUS)) ||
        (key === 'resultsView' && value !== 'map') ||
        (key === 'sort' && query === '') ||
        (key !== 'q' && !value)
      ) {
        return false;
      }
      return true;
    }),
    value =>
      isObject(value) && value.filterType === 'semantic'
        ? urlStringSemantic(value)
        : value
  );
  const queryParams = updateObject(
    searchFiltersWithNull,
    mapObject(searchParams, k => [k, null]),
    addedValues
  );

  const returnedParams = filterNulls(
    updateObject(queryString.parse(currentSearchParams), queryParams, {
      searchMode: 'true'
    })
  );
  return {
    pathname: newUrl,
    search: `?${queryString.stringify(returnedParams)}`,
    state: scrollToId ? { scrollToId } : null
  };
};

export default getSearchUrl;
